/**
 * The only place the browser talks to the server.
 *
 * Every call goes through `request`, so auth failures, network errors and
 * error-message extraction are handled once. Add new endpoints as methods on
 * the exported object rather than calling fetch from a component.
 */
import type {
  Answer, Condition, Entry, Favorite, Gender, Member, Notebook,
  Prompt, PromptSummary, Question, Story,
} from './types.ts'

export class ApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message)
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let res: Response
  try {
    res = await fetch(`/api${path}`, {
      credentials: 'same-origin',
      headers: init.body ? { 'content-type': 'application/json' } : {},
      ...init,
    })
  } catch {
    throw new ApiError('Could not reach the server. Check your connection.', 0)
  }

  const payload = await res.json().catch(() => ({}))
  if (!res.ok) {
    throw new ApiError((payload as { error?: string }).error ?? 'Something went wrong.', res.status)
  }
  return payload as T
}

const body = (data: unknown) => ({ body: JSON.stringify(data) })

export const api = {
  auth: {
    me: () => request<{ user: Member | null }>('/auth/me'),
    login: (email: string, password: string) =>
      request<{ user: Member }>('/auth/login', { method: 'POST', ...body({ email, password }) }),
    signup: (input: { email: string; password: string; displayName: string; inviteCode: string }) =>
      request<{ user: Member }>('/auth/signup', { method: 'POST', ...body(input) }),
    logout: () => request<{ ok: true }>('/auth/logout'),
    /** Always succeeds, whether or not the address belongs to anyone. */
    forgot: (email: string) =>
      request<{ ok: true; delivery: 'email' | 'unavailable' }>('/auth/forgot', { method: 'POST', ...body({ email }) }),
    checkReset: (token: string) =>
      request<{ valid: boolean }>('/auth/reset-check', { method: 'POST', ...body({ token }) }),
    reset: (token: string, password: string) =>
      request<{ user: Member }>('/auth/reset', { method: 'POST', ...body({ token, password }) }),
    members: () => request<{ members: Member[] }>('/auth/members'),
  },

  news: () => request<{ stories: Story[]; poolSize: number; refreshedAt: string }>('/news'),

  notebooks: {
    list: () => request<{ notebooks: Notebook[]; members: Member[] }>('/notebooks'),
    create: (title: string, prompt: string) =>
      request<{ notebook: Notebook }>('/notebooks', { method: 'POST', ...body({ title, prompt }) }),
    remove: (id: string) => request<{ ok: true }>(`/notebooks/${id}`, { method: 'DELETE' }),
    entries: (id: string) => request<{ entries: Entry[] }>(`/notebooks/${id}/entries`),
    write: (id: string, text: string) =>
      request<{ entry: Entry }>(`/notebooks/${id}/entries`, { method: 'POST', ...body({ body: text }) }),
    erase: (notebookId: string, entryId: string) =>
      request<{ ok: true }>(`/notebooks/${notebookId}/entries/${entryId}`, { method: 'DELETE' }),
  },

  prompts: {
    list: () => request<{ prompts: PromptSummary[]; members: Member[] }>('/prompts'),
    /** Answers come back only when the server has released them. */
    one: (id: string) =>
      request<{
        prompt: Prompt; released: boolean; memberCount: number
        answers: Answer[]; answeredBy: string[]; members: Member[]
      }>(`/prompts/${id}`),
    answer: (id: string, text: string) =>
      request<{
        prompt: Prompt; released: boolean; memberCount: number
        answers: Answer[]; answeredBy: string[]; members: Member[]
      }>(`/prompts/${id}/answers`, { method: 'POST', ...body({ body: text }) }),
    create: (text: string, kind: 'short' | 'long') =>
      request<{ prompt: Prompt }>('/prompts', { method: 'POST', ...body({ text, kind }) }),
  },

  groundwork: {
    list: () => request<{ conditions: Condition[]; questions: Question[]; members: Member[] }>('/groundwork'),
    addCondition: (text: string) =>
      request<{ condition: Condition }>('/groundwork/conditions', { method: 'POST', ...body({ text }) }),
    setMet: (id: string, met: boolean) =>
      request<{ condition: Condition }>(`/groundwork/conditions/${id}`, { method: 'PATCH', ...body({ met }) }),
    removeCondition: (id: string) =>
      request<{ ok: true }>(`/groundwork/conditions/${id}`, { method: 'DELETE' }),
    addQuestion: (text: string, person: string) =>
      request<{ question: Question }>('/groundwork/questions', { method: 'POST', ...body({ text, person }) }),
    setAnswer: (id: string, answer: string) =>
      request<{ question: Question }>(`/groundwork/questions/${id}`, { method: 'PATCH', ...body({ answer }) }),
    removeQuestion: (id: string) =>
      request<{ ok: true }>(`/groundwork/questions/${id}`, { method: 'DELETE' }),
  },

  favorites: {
    list: () => request<{ favorites: Favorite[] }>('/favorites'),
    add: (name: string, gender: Gender) =>
      request<{ favorite: Favorite }>('/favorites', { method: 'POST', ...body({ name, gender }) }),
    remove: (name: string) =>
      request<{ ok: true }>(`/favorites/${encodeURIComponent(name)}`, { method: 'DELETE' }),
  },
}
